import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useWallet } from './WalletContext';
import { 
  saveProgress as saveProgressUtil, 
  loadProgress as loadProgressUtil,
  resetProgress as resetProgressUtil,
  exportProgressBase64,
  importProgressBase64 as importProgressBase64Util,
  validateProgressData
} from '../utils/progressUtils';

const ProgressContext = createContext();

// Helper function to get concept badges based on level and XP
const getConceptBadges = (concept, level, xp) => {
  const badges = [];
  const conceptKey = concept.toLowerCase().replace(/\s+/g, '-');
  
  // Rookie badge at level 1
  if (level >= 1) {
    badges.push(`${conceptKey}-rookie`);
  }
  
  // Intermediate badges
  if (level >= 3) {
    badges.push(`${conceptKey}-apprentice`);
  }
  
  // Advanced badges
  if (level >= 5) {
    badges.push(`${conceptKey}-master`);
  }
  
  // XP-based badges
  if (xp >= 500) {
    badges.push(`${conceptKey}-expert`);
  }
  
  return badges;
};

// Helper function to get concept badge data
const getConceptBadgeData = (badgeId) => {
  const conceptBadges = {
    // Private Payments badges
    'private-payments-rookie': {
      id: 'private-payments-rookie',
      title: 'Privacy Rookie',
      description: 'Mastered the basics of private payments',
      icon: (
        <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
          <path d="M32 6 L48 16 L48 36 L32 46 L16 36 L16 16 Z" fill="rgba(15,23,42,0.8)" stroke="#8b5cf6" strokeWidth="2"/>
          <rect x="24" y="22" width="16" height="20" rx="2" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M28 22 L28 18 C28 15 30 13 32 13 C34 13 36 15 36 18 L36 22" stroke="#14b8a6" strokeWidth="2"/>
          <circle cx="32" cy="32" r="2" fill="#14b8a6"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Private Payments'
    },
    'private-payments-apprentice': {
      id: 'private-payments-apprentice',
      title: 'Privacy Apprentice',
      description: 'Level 3 in Private Payments',
      icon: (
        <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
          <path d="M32 6 L48 16 L48 36 L32 46 L16 36 L16 16 Z" fill="rgba(15,23,42,0.8)" stroke="#8b5cf6" strokeWidth="2"/>
          <rect x="24" y="22" width="16" height="20" rx="2" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M28 22 L28 18 C28 15 30 13 32 13 C34 13 36 15 36 18 L36 22" stroke="#14b8a6" strokeWidth="2"/>
          <circle cx="32" cy="32" r="3" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M30 31 L31 33 L34 30" stroke="#14b8a6" strokeWidth="1.5"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Private Payments'
    },
    'private-payments-master': {
      id: 'private-payments-master',
      title: 'Privacy Master',
      description: 'Level 5 in Private Payments',
      icon: (
        <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
          <path d="M32 6 L48 16 L48 36 L32 46 L16 36 L16 16 Z" fill="rgba(15,23,42,0.8)" stroke="#8b5cf6" strokeWidth="2"/>
          <rect x="22" y="20" width="20" height="22" rx="2" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M26 20 L26 16 C26 13 28 11 32 11 C36 11 38 13 38 16 L38 20" stroke="#14b8a6" strokeWidth="2"/>
          <circle cx="32" cy="32" r="4" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M30 31 L31.5 33 L34 30" stroke="#14b8a6" strokeWidth="2"/>
          <circle cx="24" cy="24" r="1" fill="#8b5cf6" opacity="0.5"/>
          <circle cx="40" cy="24" r="1" fill="#8b5cf6" opacity="0.5"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Private Payments'
    },
    'private-payments-expert': {
      id: 'private-payments-expert',
      title: 'Privacy Expert',
      description: '500+ XP in Private Payments',
      icon: (
        <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
          <path d="M32 6 L48 16 L48 36 L32 46 L16 36 L16 16 Z" fill="rgba(15,23,42,0.8)" stroke="#a855f7" strokeWidth="2.5"/>
          <rect x="22" y="20" width="20" height="22" rx="2" stroke="#14b8a6" strokeWidth="2.5" fill="none"/>
          <path d="M26 20 L26 16 C26 13 28 11 32 11 C36 11 38 13 38 16 L38 20" stroke="#14b8a6" strokeWidth="2.5"/>
          <circle cx="32" cy="32" r="5" stroke="#14b8a6" strokeWidth="2" fill="none"/>
          <path d="M29 31 L31 34 L35 29" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="22" cy="22" r="1.5" fill="#8b5cf6"/>
          <circle cx="42" cy="22" r="1.5" fill="#8b5cf6"/>
          <circle cx="22" cy="38" r="1.5" fill="#8b5cf6"/>
          <circle cx="42" cy="38" r="1.5" fill="#8b5cf6"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Private Payments'
    },
    // Account Recovery badges
    'account-recovery-rookie': {
      id: 'account-recovery-rookie',
      title: 'State Detective',
      description: 'Mastered the basics of account recovery',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
          <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Account Recovery'
    },
    'account-recovery-apprentice': {
      id: 'account-recovery-apprentice',
      title: 'State Detective Pro',
      description: 'Level 3 in Account Recovery',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
          <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
          <circle cx="11" cy="11" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Account Recovery'
    },
    'account-recovery-master': {
      id: 'account-recovery-master',
      title: 'State Detective Master',
      description: 'Level 5 in Account Recovery',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 11v6M9 14h6" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Account Recovery'
    },
    'account-recovery-expert': {
      id: 'account-recovery-expert',
      title: 'State Detective Expert',
      description: '500+ XP in Account Recovery',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Account Recovery'
    },
    // Secure Receipts badges
    'secure-receipts-rookie': {
      id: 'secure-receipts-rookie',
      title: 'Receipt Builder',
      description: 'Mastered the basics of secure receipts',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
          <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
          <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2"/>
          <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Secure Receipts'
    },
    'secure-receipts-apprentice': {
      id: 'secure-receipts-apprentice',
      title: 'Receipt Architect',
      description: 'Level 3 in Secure Receipts',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Secure Receipts'
    },
    'secure-receipts-master': {
      id: 'secure-receipts-master',
      title: 'Receipt Master',
      description: 'Level 5 in Secure Receipts',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
          <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Secure Receipts'
    },
    'secure-receipts-expert': {
      id: 'secure-receipts-expert',
      title: 'Receipt Expert',
      description: '500+ XP in Secure Receipts',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
          <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Secure Receipts'
    },
    // Private Proofs badges
    'private-proofs-rookie': {
      id: 'private-proofs-rookie',
      title: 'Proof Builder',
      description: 'Mastered the basics of private proofs',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Private Proofs'
    },
    'private-proofs-apprentice': {
      id: 'private-proofs-apprentice',
      title: 'Proof Architect',
      description: 'Level 3 in Private Proofs',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Private Proofs'
    },
    'private-proofs-master': {
      id: 'private-proofs-master',
      title: 'Proof Master',
      description: 'Level 5 in Private Proofs',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Private Proofs'
    },
    'private-proofs-expert': {
      id: 'private-proofs-expert',
      title: 'Proof Expert',
      description: '500+ XP in Private Proofs',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Private Proofs'
    },
    // Privacy Protection badges
    'privacy-protection-rookie': {
      id: 'privacy-protection-rookie',
      title: 'Privacy Guardian',
      description: 'Mastered the basics of privacy protection',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Privacy Protection'
    },
    'privacy-protection-apprentice': {
      id: 'privacy-protection-apprentice',
      title: 'Privacy Defender',
      description: 'Level 3 in Privacy Protection',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Privacy Protection'
    },
    'privacy-protection-master': {
      id: 'privacy-protection-master',
      title: 'Privacy Master',
      description: 'Level 5 in Privacy Protection',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Privacy Protection'
    },
    'privacy-protection-expert': {
      id: 'privacy-protection-expert',
      title: 'Privacy Expert',
      description: '500+ XP in Privacy Protection',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Privacy Protection'
    },
    // Privacy Basics badges
    'privacy-basics-rookie': {
      id: 'privacy-basics-rookie',
      title: 'Privacy Scholar',
      description: 'Mastered the basics of privacy',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Privacy Basics'
    },
    'privacy-basics-apprentice': {
      id: 'privacy-basics-apprentice',
      title: 'Privacy Student',
      description: 'Level 3 in Privacy Basics',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Privacy Basics'
    },
    'privacy-basics-master': {
      id: 'privacy-basics-master',
      title: 'Privacy Master',
      description: 'Level 5 in Privacy Basics',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Privacy Basics'
    },
    'privacy-basics-expert': {
      id: 'privacy-basics-expert',
      title: 'Privacy Expert',
      description: '500+ XP in Privacy Basics',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Privacy Basics'
    },
    // Client-Side Proving badges
    'client-side-proving-rookie': {
      id: 'client-side-proving-rookie',
      title: 'Proving Novice',
      description: 'Mastered the basics of client-side proving',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Client-Side Proving'
    },
    'client-side-proving-apprentice': {
      id: 'client-side-proving-apprentice',
      title: 'Proving Builder',
      description: 'Level 3 in Client-Side Proving',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Client-Side Proving'
    },
    'client-side-proving-master': {
      id: 'client-side-proving-master',
      title: 'Proving Master',
      description: 'Level 5 in Client-Side Proving',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Client-Side Proving'
    },
    'client-side-proving-expert': {
      id: 'client-side-proving-expert',
      title: 'Proving Expert',
      description: '500+ XP in Client-Side Proving',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Client-Side Proving'
    },
    // Transaction Flow badges
    'transaction-flow-rookie': {
      id: 'transaction-flow-rookie',
      title: 'Flow Navigator',
      description: 'Mastered the basics of transaction flow',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Transaction Flow'
    },
    'transaction-flow-apprentice': {
      id: 'transaction-flow-apprentice',
      title: 'Flow Architect',
      description: 'Level 3 in Transaction Flow',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Transaction Flow'
    },
    'transaction-flow-master': {
      id: 'transaction-flow-master',
      title: 'Flow Master',
      description: 'Level 5 in Transaction Flow',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Transaction Flow'
    },
    'transaction-flow-expert': {
      id: 'transaction-flow-expert',
      title: 'Flow Expert',
      description: '500+ XP in Transaction Flow',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Transaction Flow'
    },
    // Invisible Math badges
    'invisible-math-rookie': {
      id: 'invisible-math-rookie',
      title: 'Math Magician',
      description: 'Mastered the basics of invisible math',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'common',
      concept: 'Invisible Math'
    },
    'invisible-math-apprentice': {
      id: 'invisible-math-apprentice',
      title: 'Math Wizard',
      description: 'Level 3 in Invisible Math',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'rare',
      concept: 'Invisible Math'
    },
    'invisible-math-master': {
      id: 'invisible-math-master',
      title: 'Math Master',
      description: 'Level 5 in Invisible Math',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'epic',
      concept: 'Invisible Math'
    },
    'invisible-math-expert': {
      id: 'invisible-math-expert',
      title: 'Math Expert',
      description: '500+ XP in Invisible Math',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      rarity: 'legendary',
      concept: 'Invisible Math'
    }
  };
  
  return conceptBadges[badgeId] || null;
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

// Show storage notification to user
const showStorageNotification = (walletAddress, hasExistingProgress) => {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 80px;
    right: 20px;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
    padding: 16px 20px;
    border-radius: 12px;
    border-left: 4px solid #ff6b35;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 350px;
    animation: slideIn 0.3s ease-out;
  `;
  
  const message = hasExistingProgress 
    ? `Progress loaded for wallet ${walletAddress.slice(0, 8)}...${walletAddress.slice(-6)}`
    : `Your progress is saved locally to your browser, tied to wallet ${walletAddress.slice(0, 8)}...${walletAddress.slice(-6)}`;
  
  notification.innerHTML = `
    <div style="display: flex; align-items: flex-start; gap: 12px;">
      <div style="width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
          <polyline points="17 21 17 13 7 13 7 21"/>
          <polyline points="7 3 7 8 15 8"/>
        </svg>
      </div>
      <div>
        <div style="font-weight: 600; margin-bottom: 6px; color: #ff6b35;">Local Storage</div>
        <div style="font-size: 12px; opacity: 0.9; line-height: 1.5;">
          ${message}
          <br/>
          <span style="opacity: 0.7;">Progress does not sync across devices. Use Export/Import to backup.</span>
        </div>
      </div>
    </div>
  `;
  
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(notification);
  
  setTimeout(() => {
    notification.style.animation = 'slideIn 0.3s ease-out reverse';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 300);
  }, 6000);
};

export const ProgressProvider = ({ children }) => {
  const { connected, publicKey } = useWallet();
  const [progress, setProgress] = useState({
    tutorials: {},
    games: {},
    achievements: {},
    concepts: {}, // Track concept mastery: { 'Private Payments': { xp: 150, level: 2, badges: [] }, ... }
    stats: {
      totalTutorialsCompleted: 0,
      totalCodeExecutions: 0,
      totalGamesPlayed: 0,
      streakDays: 0,
      lastActiveDate: null,
      totalXP: 0
    }
  });

  // Load progress from localStorage when component mounts
  useEffect(() => {
    const loadInitialProgress = () => {
      const savedProgress = loadProgressUtil('guest');
      if (savedProgress) {
        // Ensure concepts property exists
        if (!savedProgress.concepts) {
          savedProgress.concepts = {};
        }
        // Ensure totalXP exists in stats
        if (!savedProgress.stats) {
          savedProgress.stats = {};
        }
        if (typeof savedProgress.stats.totalXP !== 'number') {
          savedProgress.stats.totalXP = 0;
        }
        setProgress(savedProgress);
        if (process.env.NODE_ENV === 'development') {
          console.log('Progress loaded from localStorage (guest):', savedProgress);
        }
      }
    };

    loadInitialProgress();
  }, []);

  // Load progress from wallet storage
  const loadFromWallet = useCallback(async () => {
    if (!connected || !publicKey) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Wallet not connected, skipping wallet load');
      }
      return null;
    }

    const walletAddress = publicKey.toString();
    const savedProgress = loadProgressUtil(walletAddress);
    
    if (savedProgress) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Progress loaded from wallet storage:', walletAddress);
      }
      return savedProgress;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('No saved progress found for wallet:', walletAddress);
    }
    return null;
  }, [connected, publicKey]);

  // Save progress to wallet when connected
  const saveToWallet = useCallback(async (progressData) => {
    if (!connected || !publicKey) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Wallet not connected, skipping wallet save');
      }
      return;
    }

    const walletAddress = publicKey.toString();
    saveProgressUtil(walletAddress, progressData);
  }, [connected, publicKey]);

  // Load progress from wallet when wallet connects
  useEffect(() => {
    const loadWalletProgress = async () => {
      if (connected && publicKey) {
        const walletProgress = await loadFromWallet();
        if (walletProgress) {
          // Merge wallet progress with local progress (wallet takes precedence)
          setProgress(prev => ({
            ...prev,
            ...walletProgress,
            // Merge achievements (don't overwrite, combine)
            achievements: {
              ...prev.achievements,
              ...walletProgress.achievements
            },
            // Merge tutorials
            tutorials: {
              ...prev.tutorials,
              ...walletProgress.tutorials
            },
            // Merge games
            games: {
              ...prev.games,
              ...walletProgress.games
            },
            // Use wallet stats if they're more recent or higher
            stats: {
              ...prev.stats,
              ...walletProgress.stats,
              totalTutorialsCompleted: Math.max(prev.stats.totalTutorialsCompleted, walletProgress.stats?.totalTutorialsCompleted || 0),
              totalCodeExecutions: Math.max(prev.stats.totalCodeExecutions, walletProgress.stats?.totalCodeExecutions || 0),
              totalGamesPlayed: Math.max(prev.stats.totalGamesPlayed, walletProgress.stats?.totalGamesPlayed || 0),
              streakDays: Math.max(prev.stats.streakDays, walletProgress.stats?.streakDays || 0)
            }
          }));
          if (process.env.NODE_ENV === 'development') {
            console.log('Progress synced from wallet storage');
          }
          
          // Notify user about local storage
          showStorageNotification(publicKey.toString(), true);
        } else {
          // First time connecting with this wallet
          showStorageNotification(publicKey.toString(), false);
        }
      }
    };

    loadWalletProgress();
  }, [connected, publicKey, loadFromWallet]);

  // Save progress to localStorage and wallet whenever progress changes
  useEffect(() => {
    const saveCurrentProgress = () => {
      const walletAddress = connected && publicKey ? publicKey.toString() : 'guest';
      saveProgressUtil(walletAddress, progress);
    };

    // Always save progress so updates persist immediately
    saveCurrentProgress();
  }, [progress, connected, publicKey]);

  // Award concept XP and check for badges
  const awardConceptXP = useCallback((concept, xpAmount) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      
      if (!newProgress.concepts) {
        newProgress.concepts = {};
      }
      
      if (!newProgress.concepts[concept]) {
        newProgress.concepts[concept] = {
          xp: 0,
          level: 1,
          badges: []
        };
      }

      const conceptData = newProgress.concepts[concept];
      const oldXP = conceptData.xp;
      const newXP = oldXP + xpAmount;
      
      // Level up every 100 XP
      const newLevel = Math.floor(newXP / 100) + 1;
      
      conceptData.xp = newXP;
      conceptData.level = newLevel;
      
      // Update total XP
      if (!newProgress.stats.totalXP) {
        newProgress.stats.totalXP = 0;
      }
      newProgress.stats.totalXP += xpAmount;
      
      // Check for badge unlocks
      const badges = getConceptBadges(concept, newLevel, newXP);
      badges.forEach(badgeId => {
        if (!conceptData.badges.includes(badgeId)) {
          conceptData.badges.push(badgeId);
          // Auto-claim concept badges
          if (!newProgress.achievements[badgeId]) {
            newProgress.achievements[badgeId] = {
              id: badgeId,
              earned: true,
              earnedAt: new Date().toISOString()
            };
            const badgeData = getConceptBadgeData(badgeId);
            if (badgeData) showAchievementNotification(badgeData);
          }
        }
      });
      
      return newProgress;
    });
  }, []);

  // Update tutorial progress
  const updateTutorialProgress = useCallback((tutorialId, stepId, completed = false) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      
      if (!newProgress.tutorials[tutorialId]) {
        newProgress.tutorials[tutorialId] = {
          id: tutorialId,
          currentStep: 0,
          completedSteps: [],
          completed: false,
          startedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
      }

      const tutorial = newProgress.tutorials[tutorialId];
      tutorial.lastUpdated = new Date().toISOString();
      
      if (completed && !tutorial.completedSteps.includes(stepId)) {
        tutorial.completedSteps.push(stepId);
        // Don't auto-advance currentStep - let user manually navigate with Next button
        // tutorial.currentStep = Math.max(tutorial.currentStep, stepId + 1);
      }

      // Check if tutorial is fully completed
      const tutorialData = getTutorialData(tutorialId);
      if (tutorialData && tutorial.completedSteps.length >= tutorialData.steps.length) {
        tutorial.completed = true;
        tutorial.completedAt = new Date().toISOString();
        newProgress.stats.totalTutorialsCompleted += 1;
        
        // Award concept XP if tutorial has a concept
        if (tutorialData.concept) {
          const conceptKey = tutorialData.concept;
          if (!newProgress.concepts) {
            newProgress.concepts = {};
          }
          if (!newProgress.concepts[conceptKey]) {
            newProgress.concepts[conceptKey] = {
              xp: 0,
              level: 1,
              badges: []
            };
          }
          
          const conceptData = newProgress.concepts[conceptKey];
          const oldXP = conceptData.xp;
          const newXP = oldXP + 50;
          const newLevel = Math.floor(newXP / 100) + 1;
          
          conceptData.xp = newXP;
          conceptData.level = newLevel;
          
          // Update total XP
          if (!newProgress.stats.totalXP) {
            newProgress.stats.totalXP = 0;
          }
          newProgress.stats.totalXP += 50;
          
          // Check for badge unlocks
          const badges = getConceptBadges(conceptKey, newLevel, newXP);
          badges.forEach(badgeId => {
            if (!conceptData.badges.includes(badgeId)) {
              conceptData.badges.push(badgeId);
              // Auto-claim concept badges
              if (!newProgress.achievements[badgeId]) {
                newProgress.achievements[badgeId] = {
                  id: badgeId,
                  earned: true,
                  earnedAt: new Date().toISOString()
                };
                const badgeData = getConceptBadgeData(badgeId);
                if (badgeData) showAchievementNotification(badgeData);
              }
            }
          });
        }
      }

      return newProgress;
    });
  }, []);

  // Update game progress
  const updateGameProgress = useCallback((gameId, score, level = null, completed = false, concept = null) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      
      if (!newProgress.games[gameId]) {
        newProgress.games[gameId] = {
          id: gameId,
          highScore: 0,
          level: 1,
          completed: false,
          attempts: 0,
          startedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
      }

      const game = newProgress.games[gameId];
      const wasAlreadyCompleted = game.completed;
      game.lastUpdated = new Date().toISOString();
      game.attempts += 1;
      
      if (score > game.highScore) {
        game.highScore = score;
      }
      
      if (level !== null) {
        game.level = level;
      }
      
      if (completed) {
        game.completed = true;
        game.completedAt = new Date().toISOString();
        newProgress.stats.totalGamesPlayed += 1;
        
        // Award concept XP when game is completed for the first time
        if (concept && !wasAlreadyCompleted) {
          const conceptKey = concept;
          if (!newProgress.concepts) {
            newProgress.concepts = {};
          }
          if (!newProgress.concepts[conceptKey]) {
            newProgress.concepts[conceptKey] = {
              xp: 0,
              level: 1,
              badges: []
            };
          }
          
          const conceptData = newProgress.concepts[conceptKey];
          const oldXP = conceptData.xp;
          const newXP = oldXP + 50; // Award 50 XP for completing a game
          const newLevel = Math.floor(newXP / 100) + 1;
          
          conceptData.xp = newXP;
          conceptData.level = newLevel;
          
          // Update total XP
          if (!newProgress.stats.totalXP) {
            newProgress.stats.totalXP = 0;
          }
          newProgress.stats.totalXP += 50;
          
          // Check for badge unlocks
          const badges = getConceptBadges(conceptKey, newLevel, newXP);
          badges.forEach(badgeId => {
            if (!conceptData.badges.includes(badgeId)) {
              conceptData.badges.push(badgeId);
              // Auto-claim concept badges
              if (!newProgress.achievements[badgeId]) {
                newProgress.achievements[badgeId] = {
                  id: badgeId,
                  earned: true,
                  earnedAt: new Date().toISOString()
                };
                const badgeData = getConceptBadgeData(badgeId);
                if (badgeData) showAchievementNotification(badgeData);
              }
            }
          });
        }
      } else {
        // Reset completed flag when explicitly set to false
        // This ensures that moving to a new level resets the completion status
        game.completed = false;
      }

      return newProgress;
    });
  }, []);

  // Determine eligibility (no auto-claiming)
  const isAchievementEligible = useCallback((achievementId) => {
    const s = progress.stats;
    const g = progress.games?.['logic-maze'] || {};
    switch (achievementId) {
      case 'tutorial-complete': return s.totalTutorialsCompleted >= 1;
      case 'advanced-student': return s.totalTutorialsCompleted >= 5;
      case 'challenger': return s.totalGamesPlayed >= 1;
      case 'warrior': return s.totalGamesPlayed >= 5;
      case 'week-warrior': return s.streakDays >= 7;
      case 'month-master': return s.streakDays >= 30;
      case 'maze-runner': return (g.level || 1) >= 6 || Boolean(g.completed);
      case 'no-help-hero': return (Boolean(g.completed) || (g.level || 1) >= 6) && Number(g.usedHints || 0) === 0;
      default: return false;
    }
  }, [progress.stats, progress.games]);

  // Manual claim
  const claimAchievement = useCallback((achievementId) => {
    setProgress(prev => {
      const next = { ...prev };
      if (!next.achievements[achievementId]) {
        next.achievements[achievementId] = { id: achievementId, earned: true, earnedAt: new Date().toISOString() };
      } else {
        next.achievements[achievementId] = { ...next.achievements[achievementId], earned: true, earnedAt: new Date().toISOString() };
      }
      return next;
    });
    const achievement = getAchievementData(achievementId);
    if (achievement) showAchievementNotification(achievement);
  }, []);

  // Execute code and track progress
  const executeCode = useCallback(async (code, tutorialId = null, stepId = null) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      newProgress.stats.totalCodeExecutions += 1;
      newProgress.stats.lastActiveDate = new Date().toISOString();
      
      // Update streak
      const today = new Date().toDateString();
      const lastActive = prev.stats.lastActiveDate ? new Date(prev.stats.lastActiveDate).toDateString() : null;
      
      if (lastActive !== today) {
        if (lastActive === new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString()) {
          newProgress.stats.streakDays += 1;
        } else if (lastActive !== today) {
          newProgress.stats.streakDays = 1;
        }
      }

      return newProgress;
    });

    // If this is part of a tutorial, update tutorial progress
    if (tutorialId !== null && stepId !== null) {
      updateTutorialProgress(tutorialId, stepId, true);
    }

    // Achievement eligibility updates are computed live in the Achievements page

    // Save to wallet if connected
    if (connected) {
      await saveToWallet(progress);
    }
  }, [updateTutorialProgress, saveToWallet, connected, progress]);

  // Check achievements whenever progress changes
  useEffect(() => {
    // No auto-claiming; page will compute eligibility
  }, [progress.stats]);

  // Check achievements on mount to catch any already earned
  useEffect(() => {
    // No auto-claiming on mount
  }, []);

  // Get progress for specific tutorial/game
  const getProgress = useCallback((type, id) => {
    return progress[type]?.[id] || null;
  }, [progress]);

  // Get all achievements
  const getAchievements = useCallback(() => {
    return Object.values(progress.achievements);
  }, [progress.achievements]);

  // Check if achievement is earned
  const isAchievementEarned = useCallback((achievementId) => {
    const earned = progress.achievements[achievementId]?.earned || false;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Checking achievement ${achievementId}:`, earned, progress.achievements[achievementId]);
    }
    return earned;
  }, [progress.achievements]);

  // Generic updater for progress (supports function or partial object)
  const updateProgress = useCallback((update) => {
    setProgress(prev => {
      if (typeof update === 'function') {
        return update(prev);
      }
      const next = { ...prev };
      if (update && typeof update === 'object') {
        if (update.stats) {
          next.stats = { ...prev.stats, ...update.stats };
        }
        if (update.tutorials) {
          next.tutorials = { ...prev.tutorials, ...update.tutorials };
        }
        if (update.games) {
          next.games = { ...prev.games, ...update.games };
        }
        if (update.achievements) {
          next.achievements = { ...prev.achievements, ...update.achievements };
        }
      }
      return next;
    });
  }, []);

  // Export progress as JSON (for backup)
  const exportProgress = useCallback(() => {
    const walletAddress = connected && publicKey ? publicKey.toString() : 'guest';
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      walletAddress,
      progress
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `midenhub-progress-${walletAddress}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return exportData;
  }, [progress, connected, publicKey]);

  // Import progress from JSON file 
  const importProgress = useCallback((jsonData) => {
    try {
      // Parse JSON if string
      const imported = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      // Check for progress field
      if (!imported.progress) {
        throw new Error('Invalid file format: missing progress data');
      }
      
      // STRICT VALIDATION - will throw if data is malformed or malicious
      validateProgressData(imported.progress);
      
      // Only merge if validation passes
      setProgress(prev => ({
        tutorials: { ...prev.tutorials, ...imported.progress.tutorials },
        games: { ...prev.games, ...imported.progress.games },
        achievements: { ...prev.achievements, ...imported.progress.achievements },
        concepts: { ...prev.concepts, ...(imported.progress.concepts || {}) },
        stats: {
          totalTutorialsCompleted: Math.max(prev.stats.totalTutorialsCompleted, imported.progress.stats.totalTutorialsCompleted || 0),
          totalCodeExecutions: Math.max(prev.stats.totalCodeExecutions, imported.progress.stats.totalCodeExecutions || 0),
          totalGamesPlayed: Math.max(prev.stats.totalGamesPlayed, imported.progress.stats.totalGamesPlayed || 0),
          streakDays: Math.max(prev.stats.streakDays, imported.progress.stats.streakDays || 0),
          lastActiveDate: imported.progress.stats.lastActiveDate || prev.stats.lastActiveDate,
          totalXP: Math.max(prev.stats.totalXP || 0, imported.progress.stats.totalXP || 0)
        }
      }));
      
      return { success: true, message: 'Progress imported successfully!' };
    } catch (error) {
      console.error('Import validation failed:', error);
      return { 
        success: false, 
        message: error.message || 'Invalid or corrupt file. Only import files exported from this app.'
      };
    }
  }, []);

  // Export progress as base64 string (for copy-paste)
  const exportProgressString = useCallback(() => {
    const walletAddress = connected && publicKey ? publicKey.toString() : 'guest';
    return exportProgressBase64(walletAddress);
  }, [connected, publicKey]);

  // Import progress from base64 string (for copy-paste)
  const importProgressString = useCallback((encodedData) => {
    try {
      const walletAddress = connected && publicKey ? publicKey.toString() : 'guest';
      const result = importProgressBase64Util(walletAddress, encodedData);
      
      if (result.success) {
        // Reload progress after import
        const imported = loadProgressUtil(walletAddress);
        if (imported) {
          setProgress(imported);
        }
        return { success: true, message: 'Progress restored successfully!' };
      }
      
      return result;
    } catch (error) {
      console.error('Import from string failed:', error);
      return { success: false, message: 'Invalid backup code' };
    }
  }, [connected, publicKey]);

  // Reset all progress for current wallet
  const resetAllProgress = useCallback(() => {
    const walletAddress = connected && publicKey ? publicKey.toString() : 'guest';
    const result = resetProgressUtil(walletAddress);
    
    if (result.success) {
      // Reset to default state
      setProgress({
        tutorials: {},
        games: {},
        achievements: {},
        concepts: {},
        stats: {
          totalTutorialsCompleted: 0,
          totalCodeExecutions: 0,
          totalGamesPlayed: 0,
          streakDays: 0,
          lastActiveDate: null,
          totalXP: 0
        }
      });
      return { success: true, message: 'Progress reset successfully!' };
    }
    
    return result;
  }, [connected, publicKey]);

  const value = {
    progress,
    updateProgress,
    updateTutorialProgress,
    updateGameProgress,
    awardConceptXP,
    getConceptBadgeData,
    executeCode,
    getProgress,
    getAchievements,
    isAchievementEarned,
    isAchievementEligible,
    claimAchievement,
    saveToWallet,
    exportProgress,
    importProgress,
    exportProgressString,
    importProgressString,
    resetAllProgress
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

// Helper function to get tutorial data
// Using a lazy import 
let tutorialsCache = null;
let tutorialsLoading = false;
const getTutorialData = (tutorialId) => {
  if (!tutorialId) return null;
  try {
    if (!tutorialsCache && !tutorialsLoading) {
      
      try {
        const tutorialsModule = require('../data/tutorials');
        tutorialsCache = tutorialsModule.tutorials || [];
      } catch (e) {
        // If require fails, return null - the component will handle it
        if (process.env.NODE_ENV === 'development') {
          console.warn('Could not load tutorial data synchronously:', e);
        }
        return null;
      }
    }
    if (tutorialsCache) {
      return tutorialsCache.find(t => t && t.id === tutorialId) || null;
    }
    return null;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Could not load tutorial data:', e);
    }
    return null;
  }
};

// Helper function to get achievement data
const getAchievementData = (achievementId) => {
  const achievements = {
    'tutorial-complete': {
      id: 'tutorial-complete',
      title: 'Tutorial Graduate',
      description: 'Complete your first tutorial',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="28" fill="rgba(15,23,42,0.8)" stroke="#14b8a6" stroke-width="2"/><path d="M20 36 L28 36 L28 32 L36 32 L36 36 L44 36" stroke="#14b8a6" stroke-width="1.5" fill="none"/><path d="M26 28 L32 22 L38 28 L38 24 L32 18 L26 24 Z" fill="#14b8a6"/><rect x="29" y="36" width="6" height="4" rx="1" fill="#14b8a6" opacity="0.6"/></svg>',
      rarity: 'common'
    },
    'advanced-student': {
      id: 'advanced-student',
      title: 'Advanced Student',
      description: 'Complete 5 tutorials',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="28" fill="rgba(15,23,42,0.8)" stroke="#14b8a6" stroke-width="2"/><path d="M20 38 L28 38 L28 34 L36 34 L36 38 L44 38" stroke="#14b8a6" stroke-width="1.5" fill="none"/><path d="M26 30 L26 22 L30 18 L34 18 L38 22 L38 30" stroke="#8b5cf6" stroke-width="2" fill="none"/><path d="M28 24 L32 20 L36 24" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      rarity: 'rare'
    },
    'challenger': {
      id: 'challenger',
      title: 'First Game',
      description: 'Complete your first game',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f59e0b" stroke-width="2"/><path d="M32 20 L36 28 L45 29 L38 35 L40 44 L32 39 L24 44 L26 35 L19 29 L28 28 Z" fill="#f59e0b"/></svg>',
      rarity: 'common'
    },
    'warrior': {
      id: 'warrior',
      title: 'Game Warrior',
      description: 'Complete 5 games',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f59e0b" stroke-width="2"/><path d="M22 24 L28 18 L28 34" stroke="#ef4444" stroke-width="2"/><path d="M42 24 L36 18 L36 34" stroke="#ef4444" stroke-width="2"/><circle cx="28" cy="36" r="2" fill="#f59e0b"/><circle cx="36" cy="36" r="2" fill="#f59e0b"/></svg>',
      rarity: 'rare'
    },
    'week-warrior': {
      id: 'week-warrior',
      title: 'Week Warrior',
      description: 'Maintain a 7-day streak',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M16 12 L48 12 L52 32 L48 52 L16 52 L12 32 Z" fill="rgba(15,23,42,0.8)" stroke="#ef4444" stroke-width="2"/><path d="M32 18 L28 28 L36 28 L30 38 L34 38 L38 28 L32 28 Z" fill="#f59e0b"/><circle cx="24" cy="24" r="1" fill="#f59e0b" opacity="0.5"/><circle cx="40" cy="24" r="1" fill="#f59e0b" opacity="0.5"/><circle cx="24" cy="40" r="1" fill="#f59e0b" opacity="0.5"/><circle cx="40" cy="40" r="1" fill="#f59e0b" opacity="0.5"/></svg>',
      rarity: 'rare'
    },
    'month-master': {
      id: 'month-master',
      title: 'Month Master',
      description: 'Maintain a 30-day streak',
      icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M16 12 L48 12 L52 32 L48 52 L16 52 L12 32 Z" fill="rgba(15,23,42,0.8)" stroke="#f59e0b" stroke-width="2"/><path d="M24 38 L24 44 L40 44 L40 38 L38 36 L26 36 Z" fill="#f59e0b"/><rect x="26" y="30" width="12" height="6" rx="1" fill="#f59e0b"/><path d="M28 24 L32 20 L36 24" stroke="#f59e0b" stroke-width="2" fill="none"/><path d="M26 18 L28 22 L30 18 L32 22 L34 18 L36 22 L38 18" stroke="#f59e0b" stroke-width="1.5" fill="none"/></svg>',
      rarity: 'legendary'
    },
      'maze-runner': {
        id: 'maze-runner',
        title: 'Maze Runner',
        description: 'Complete all Logic Maze levels',
        icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" stroke-width="2"/><rect x="22" y="20" width="20" height="20" rx="2" stroke="#475569" stroke-width="1.5" fill="none"/><path d="M24 22 L26 22 L26 26 L30 26 L30 22 L34 22 L34 30 L38 30 L38 38 L40 38" stroke="#f97316" stroke-width="2" stroke-linecap="round"/><circle cx="40" cy="38" r="1.5" fill="#f97316"/><circle cx="24" cy="22" r="1" fill="#10b981"/></svg>',
        rarity: 'rare'
      },
      'no-help-hero': {
        id: 'no-help-hero',
        title: 'No-Help Hero',
        description: 'Finish Logic Maze without using any hints',
        icon: '<svg width="24" height="24" viewBox="0 0 64 64" fill="none"><path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" stroke-width="2"/><circle cx="32" cy="28" r="8" stroke="#8b5cf6" stroke-width="2" fill="none"/><path d="M30 26 Q32 24 34 26" stroke="#8b5cf6" stroke-width="1.5" fill="none"/><circle cx="32" cy="34" r="1" fill="#8b5cf6"/><line x1="24" y1="20" x2="40" y2="36" stroke="#ef4444" stroke-width="2.5"/></svg>',
        rarity: 'epic'
      }
  };
  
  return achievements[achievementId];
};

// Show achievement notification
const showAchievementNotification = (achievement) => {
  // Create a simple notification
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: rgba(30, 41, 59, 0.95);
    border: 1px solid rgba(249, 115, 22, 0.3);
    color: white;
    padding: 16px 20px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    animation: slideIn 0.3s ease-out;
  `;
  
  // Create container for icon and text
  const container = document.createElement('div');
  container.style.cssText = 'display: flex; align-items: center; gap: 12px;';
  
  // Create icon container
  const iconContainer = document.createElement('div');
  iconContainer.style.cssText = 'width: 48px; height: 48px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #f97316;';
  
  // Handle React element icons or string icons
  if (React.isValidElement(achievement.icon)) {
    // Convert React element to HTML string
    iconContainer.innerHTML = ReactDOMServer.renderToString(achievement.icon);
  } else if (achievement.icon && achievement.icon.startsWith('<svg')) {
    // Handle SVG string icons
    iconContainer.innerHTML = achievement.icon;
  } else {
    // Handle emoji or other text icons
    iconContainer.innerHTML = `<div style="font-size: 24px;">${achievement.icon}</div>`;
  }
  
  // Create text container
  const textContainer = document.createElement('div');
  textContainer.innerHTML = `
        <div style="font-weight: 600; margin-bottom: 4px;">Achievement Unlocked!</div>
        <div style="font-size: 12px; opacity: 0.9;">${achievement.title}</div>
  `;
  
  container.appendChild(iconContainer);
  container.appendChild(textContainer);
  notification.appendChild(container);
  
  // Add animation styles
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(notification);
  
  // Remove notification after 4 seconds
  setTimeout(() => {
    notification.style.animation = 'slideIn 0.3s ease-out reverse';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 300);
  }, 4000);
};
